import {
    UserDetails, UserInfoProps,
    UserRights,
} from './types'

import {
    mapMessageActionToModalTitleText,
    messageAction,
} from 'components/Messages/helpers'

import {
    useAppDispatch, useAppSelector,
} from 'hooks'

import ConfirmationPromiseModal from 'components/Modal/ConfirmationPromiseModal'
import {MessageActionTypes} from 'components/Messages/types'
import {NinchatStoreUser} from 'api/types/store'
import React from 'react'
import UserInfoView from './UserInfoView'
import {addMember} from 'api/action/addMember'
import {getPermissions} from 'selectors/ninchat/permissions'
import {isRealmOwnerOrOperator} from 'utils/realm/realm'
import {navigation} from '../../navigation'
import {toast} from 'components/Toast'
import {updateMember} from 'api/action/updateMember'
import {usePromiseModal} from 'components/Modal/PromiseModal/hooks/usePromiseModal'
import {useTranslation} from 'react-i18next'
import {useUsers} from '../../hooks/ninchat'

const getUserDetails = (user?: NinchatStoreUser): UserDetails => {
    const {
        user_attrs = {
            deleted: false,
            iconurl: '',
            info: {},
            name: '',
            realname: '',
        },
    } = user ?? {}

    const {t} = useTranslation()
    const name = user_attrs.realname
        ? user_attrs.realname :
        user_attrs.name ? user_attrs.name : user_attrs.deleted ? t('Deleted user') : t('Unknown user')

    return {
        company: user_attrs.info?.company,
        iconURL: user_attrs.iconurl,
        jobTitle: user_attrs.info?.job_title,
        name,
        websiteUrl: user_attrs.info?.url,
    }
}

const UserInfo = (props: UserInfoProps): React.ReactElement => {
    const {
        channelId,
        light = false,
        size = 'big',
        userId,
    } = props

    const user = useUsers([userId])?.[userId]
    const dispatch = useAppDispatch()
    const {t} = useTranslation()
    const currentUserIsModerator = useAppSelector(({ninchat}) => ninchat.user_channels[channelId || '']?.channel_members?.[ninchat.user_id || '']?.member_attrs?.moderator)
    const currentUserIsOwner = useAppSelector(({ninchat}) => ninchat.user_channels[channelId || '']?.channel_attrs?.owner_id === ninchat.user_id)
    const currentUserIsOperator = useAppSelector(({ninchat}) => ninchat.user_channels[channelId || '']?.channel_members?.[ninchat.user_id || '']?.member_attrs?.operator)
    const isModerationActionConfirmationDisabled = useAppSelector(({ninchat}) => !!ninchat.user_settings.disable_moderation_action_confirmations)
    const {
        autohide, moderator, operator, silenced,
    } = useAppSelector(({ninchat}) => ninchat?.user_channels?.[channelId ?? '']?.channel_members?.[userId]?.member_attrs ?? {})
    const realmId = useAppSelector(({ninchat}) => ninchat.user_channels[channelId || '']?.realm_id)
    const permissionsResult = useAppSelector((state) => getPermissions(state, realmId))
    const hasNewPermissionsEnabled = useAppSelector(({ninchat}) => !!ninchat.user_realms?.[realmId || '']?.realm_attrs?.permissions_enabled)
    const canUseLegacyRealmPermission = useAppSelector(({ninchat}) => isRealmOwnerOrOperator(ninchat, realmId))
    const userAlreadyInOrganization = useAppSelector(({ninchat}) => userId in (ninchat.user_realms?.[realmId || '']?.realm_members ?? {}))

    const canChangeRights = currentUserIsOperator || currentUserIsOwner
    const currentUserId = useAppSelector(({ninchat}) => ninchat.user_id)

    // eslint-disable-next-line no-extra-parens
    const disableRemoveOperator = currentUserIsOperator && (userId === currentUserId)
    const canAddUserToOrganization = !!realmId && !userAlreadyInOrganization && (hasNewPermissionsEnabled ?
        permissionsResult?.includes('manage_realm') :
        canUseLegacyRealmPermission)

    const openConfirmationPromiseModal = usePromiseModal(ConfirmationPromiseModal)

    const loadDialogue = (dialogue_id: string): void => {
        navigation.push('Dialogue', {dialogue_id})
    }

    const onAction = (action: MessageActionTypes) => {
        messageAction({
            action,
            channelId: channelId || '',
            dispatch,
            message: {message_user_id: userId},
            t,
        })
    }

    const toggleRights = (rights: UserRights, state: boolean) => {
        dispatch(updateMember(
            {
                channel_id: channelId,
                member_attrs: {
                    ...rights === UserRights.operator ? {operator: state} : {},
                    ...rights === UserRights.moderator ? {moderator: state} : {},

                },
                user_id: userId,
            },
        ))
            .unwrap()
            .then(() => {
                if (state) {
                    toast.success(t('Rights granted'))
                } else {
                    toast.success(t('Rights removed'))
                }
            }, () => {
                if (state) {
                    toast.success(t('Error granting rights'))
                } else {
                    toast.success(t('Error removing rights'))
                }
            })
    }

    const onAddToOrganization = () => {
        if (!realmId) {
            return
        }

        dispatch(addMember({
            realm_id: realmId,
            user_id: userId,
        }))
            .unwrap()
            .then(() => {
                toast.success(t('Member added'))
            }, () => {
                toast.error(t('Error adding member'))
            })
    }

    const handleChannelAction = (action: MessageActionTypes) => () => {
        if (isModerationActionConfirmationDisabled) {
            // eslint-disable-next-line no-unused-expressions
            onAction && onAction(action)
            return
        }

        const {
            title, text,
        } = mapMessageActionToModalTitleText(action, t)

        openConfirmationPromiseModal({
            text,
            title,
        }).then(() => {
            // eslint-disable-next-line no-unused-expressions
            onAction && onAction(action)
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        }, () => {})
    }

    const {
        iconURL, company, jobTitle, websiteUrl, name,
    } = getUserDetails(user)

    const translations = {
        addToOrganization: t('Add to organization'),
        externalLink: t('External link'),
        for15Minutes: t('for 15 minutes'),
        for1Hour: t('for 1 hour'),
        for24Hours: t('for 24 hours'),
        for5Minutes: t('for 5 minutes'),
        giveModerationRights: t('Give moderator rights'),
        giveOperatorRights: t('Give operator rights'),
        hideAllMessages: t('Hide all messages from this user'),
        hideNewMessages: t('Hide new messages from this user'),
        moderator: t('Moderator'),
        operator: t('Operator'),
        removeModerationRights: t('Remove moderator rights'),
        removeOperatorRights: t('Remove operator rights'),
        removeUserFromChannel: t('Remove user from this channel'),
        showNewMessages: t('Show new messages from this user'),
        silenceUser: t('Silence user'),
        startPrivateConversation: t('Start private conversation'),
        unsilenceUser: t('Unsilence user'),
        untilUnsilenced: t('until unsilenced'),
    }

    return (
        <UserInfoView
            autohide={autohide}
            canAddToOrganization={canAddUserToOrganization}
            canChangeRights={canChangeRights}
            canModerate={!!currentUserIsModerator}
            company={company}
            disableRemoveOperator={!!disableRemoveOperator}
            handleChannelAction={handleChannelAction}
            iconURL={iconURL}
            jobTitle={jobTitle}
            light={light}
            loadDialogue={loadDialogue}
            moderator={moderator}
            name={name}
            onAddToOrganization={onAddToOrganization}
            operator={operator}
            silenced={silenced}
            size={size}
            toggleRights={toggleRights}
            translations={translations}
            userId={userId}
            websiteUrl={websiteUrl}
        />
    )
}

export default UserInfo
